import React, { useState, useRef, useEffect } from 'react';
import { EXPERIENCE } from '../constants';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const TimelineItem: React.FC<{ job: typeof EXPERIENCE[0], isFirst: boolean, isLast: boolean }> = ({ job, isFirst, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate how far through the element the scroll is
      // We want the "electricity" to follow as the element passes through the center-ish of the screen
      const start = viewportHeight * 0.9;
      const end = viewportHeight * 0.3;
      
      const progress = Math.min(Math.max((start - rect.top) / (start - end), 0), 1);
      setScrollProgress(progress);
      
      // Basic visibility for the entry animation
      if (rect.top < viewportHeight * 0.95) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div 
      ref={elementRef}
      className={`relative pl-8 md:pl-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Background Static Line */}
      <div className={`absolute left-[3px] md:left-[43px] top-0 bottom-0 w-[2.5px] bg-gray-100/80 z-0 ${isFirst ? 'top-8' : ''} ${isLast ? 'h-8' : ''}`} />
      
      {/* "Electricity" Animated Line - Reverses on Scroll Up */}
      <div 
        className="absolute left-[3px] md:left-[43px] top-0 w-[2.5px] z-1 transition-all duration-100 ease-out origin-top"
        style={{ 
          height: `${scrollProgress * 100}%`,
          background: `linear-gradient(to bottom, transparent, ${job.color}, ${job.color})`,
          boxShadow: scrollProgress > 0 ? `0 0 15px ${job.color}60` : 'none',
          willChange: 'height'
        }}
      />

      {/* Main Node Dot */}
      <div 
        className="absolute left-[-2px] md:left-[38px] top-7 w-3 md:w-4 h-3 md:h-4 rounded-full z-10 ring-4 ring-white shadow-md transition-all duration-500"
        style={{ 
          backgroundColor: scrollProgress > 0.1 ? job.color : '#f3f4f6',
          transform: isVisible ? `scale(${0.8 + (scrollProgress * 0.4)})` : 'scale(0)',
          boxShadow: scrollProgress > 0.1 ? `0 0 10px ${job.color}40` : 'none'
        }}
      />

      {/* Date Badge - Mobile */}
      <div className="md:hidden inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest text-white mb-4 shadow-sm" style={{ backgroundColor: job.color }}>
        {job.period}
      </div>

      <div className="grid md:grid-cols-4 gap-4 md:gap-12 items-start group mb-16">
        {/* Sidebar Date/Company */}
        <div className="hidden md:block md:col-span-1 pt-1">
          <div 
            className="inline-block px-4 py-1.5 rounded-full text-[11px] font-black tracking-tighter text-white shadow-sm mb-4 transition-transform duration-500" 
            style={{ 
              backgroundColor: job.color,
              transform: isVisible ? 'translateX(0)' : 'translateX(-20px)'
            }}
          >
            {job.period}
          </div>
          <p aria-hidden="true" className="text-xl font-serif font-bold text-gray-900 group-hover:text-gray-600 transition-colors">{job.company}</p>
        </div>

        {/* Job Details Content */}
        <div className="md:col-span-3">
          <div className="md:hidden">
            <p aria-hidden="true" className="text-xl font-serif font-bold text-gray-900 mb-1">{job.company}</p>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">{job.role}<span className="sr-only"> at {job.company}, {job.period}</span></h3>
          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            {job.description}
          </p>

          {job.milestones && (
            <div className="space-y-4">
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all py-2 border-b-2 border-transparent hover:border-gray-900 active:scale-95"
              >
                {isExpanded ? (
                  <>Hide Milestones <ChevronUp size={16} /></>
                ) : (
                  <>View Career Milestones ({job.milestones.length}) <ChevronDown size={16} /></>
                )}
              </button>

              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[1200px] opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
                <div className="grid gap-4">
                  {job.milestones.map((milestone, idx) => (
                    <div key={idx} className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all relative overflow-hidden group/milestone hover:translate-x-1">
                      {/* Accent Strip */}
                      <div className="absolute top-0 left-0 w-1.5 h-full opacity-20 group-hover/milestone:opacity-100 transition-opacity" style={{ backgroundColor: job.color }} />
                      
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-[10px] font-black tracking-widest text-gray-400 uppercase">{milestone.date}</span>
                        {milestone.link && (
                          <a 
                            href={milestone.link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[10px] font-bold flex items-center gap-1.5 px-3 py-1 bg-gray-50 rounded-full hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-100 transition-all"
                            style={{ color: job.color }}
                          >
                            {milestone.link.label} <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1 leading-snug">{milestone.title}</h4>
                      <p className="text-sm text-gray-500 leading-relaxed">{milestone.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Timeline: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto px-4">
      <div className="relative">
        {/* Glow effect at start of timeline */}
        <div className="absolute left-[3px] md:left-[43px] top-0 w-[10px] h-[10px] -translate-x-[4px] bg-pink-400 rounded-full blur-md opacity-50 z-20" />
        
        {EXPERIENCE.map((job, index) => (
          <TimelineItem 
            key={index} 
            job={job} 
            isFirst={index === 0}
            isLast={index === EXPERIENCE.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default Timeline;